/**
 * MP4s from a run's screen recordings (fn(args, deps) pattern): one per passing
 * scenario that Playwright recorded (`story.init(..., { featureVideo: true })`),
 * with the blank page load cut off the start, and a `<name>.video.json` manifest
 * beside it that executable-stories-remotion reads: size, length, each click and
 * focused field, and when each step began. Same naming as `gif`.
 */

import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import type { TestCaseResult } from "executable-stories-core/types/test-result";
import { scenarioSlug } from "executable-stories-core/utils/slug";

export interface VideoArgs {
  testCases: TestCaseResult[];
  outputDir: string;
  /** Cut the blank page off the start. Default true. */
  trimLeadIn?: boolean;
}

export interface VideoDeps {
  /** Runs ffmpeg or ffprobe; resolves with their output, rejects when it fails or is missing. */
  exec(command: "ffmpeg" | "ffprobe", args: string[]): Promise<{ stdout: string; stderr: string }>;
}

/** `<name>.video.json`: the shape executable-stories-remotion's `VideoManifest` reads. */
export interface VideoManifest {
  name: string;
  description?: string;
  video: string;
  width: number;
  height: number;
  durationMs: number;
  /** Clicks and focused fields: ms into the video, pixels in it. */
  clicks: { t: number; x: number; y: number; kind: "click" | "focus" }[];
  /** Each step, from when it began: ms into the video. Steps before the trim start at 0. */
  steps: { t: number; keyword: string; text: string }[];
}

export interface VideoResult {
  written: { scenario: string; video: string; manifest: string; trimmedMs: number }[];
  /** Scenarios left out, with why: not passing, or not recorded. */
  skipped: { scenario: string; reason: string }[];
}

/** One frame: its time, and the share of it (0 to 100) that is near-white. */
interface FrameCoverage {
  t: number;
  white: number;
}

/**
 * Parses ffmpeg's `negate,blackframe=amount=0` log: after negating, the near-white
 * pixels are the "black" ones, so `pblack` is how much of the frame is blank page.
 */
export function parseFrames(log: string): FrameCoverage[] {
  return [...log.matchAll(/pblack:(\d+).*? t:([\d.]+)/g)].map((m) => ({ t: Number(m[2]), white: Number(m[1]) }));
}

/**
 * Seconds until the first frame that shows the app: Playwright's blank page is
 * white, so it ends at the first frame with 2% or more of it not white. A drawn
 * cursor (showcase's `showCursor`) covers well under 1%, so it doesn't count.
 * For an app whose first screen is near-empty white, pass `--no-trim`.
 */
export function leadInSeconds(frames: FrameCoverage[]): number {
  return frames.find((f) => f.white < 99)?.t ?? 0;
}

/** The recording a scenario carries, as a file path; inline (small) recordings are written to `tmp`. */
function recordingFile(tc: TestCaseResult, tmp: string): string | undefined {
  const att = tc.attachments.find((a) => a.mediaType.startsWith("video/"));
  if (!att) return undefined;
  if (att.external || att.contentEncoding === "IDENTITY") return fs.existsSync(att.body) ? att.body : undefined;
  const file = path.join(tmp, "recording.webm");
  fs.writeFileSync(file, Buffer.from(att.body, "base64"));
  return file;
}

/**
 * The manifest for a recording `size` pixels wide with `trimmedMs` cut off. Times
 * count from when recording began, which the adapter notes in `story.recording`;
 * without it the manifest has no clicks or steps.
 */
export function videoManifest(
  tc: TestCaseResult,
  { name, size, durationMs, trimmedMs }: { name: string; size: { width: number; height: number }; durationMs: number; trimmedMs: number },
): VideoManifest {
  const recording = tc.story.recording;
  const origin = recording?.startedAtMs;
  const at = (ms: number) => Math.round(ms - (origin ?? 0) - trimmedMs);
  // Playwright fits the viewport inside the video, top-left, so the tighter side sets the scale:
  // a portrait viewport in a landscape video is scaled by height. It only scales down: a phone
  // viewport in a larger video stays 1:1 in the corner, with the rest padded. After a resize it
  // fits the new size the same way, so each point uses the viewport it happened in.
  const ratio = (viewport = recording?.viewport) =>
    viewport ? Math.min(1, size.width / viewport.width, size.height / viewport.height) : 1;
  return {
    name,
    description: tc.story.scenario,
    video: `${name}.mp4`,
    width: size.width,
    height: size.height,
    durationMs: Math.round(durationMs),
    clicks:
      origin === undefined
        ? []
        : (recording?.attention ?? [])
            .map((p) => {
              const r = ratio(p.viewport);
              return { t: at(p.at), x: Math.round(p.x * r), y: Math.round(p.y * r), kind: p.kind };
            })
            .filter((c) => c.t >= 0 && c.t <= durationMs && c.x >= 0 && c.y >= 0 && c.x < size.width && c.y < size.height),
    steps:
      origin === undefined
        ? []
        : tc.story.steps
            .filter((s) => s.startedAtMs !== undefined)
            .map((s) => ({ t: Math.max(0, at(s.startedAtMs!)), keyword: s.keyword, text: s.text })),
  };
}

export async function writeVideos(args: VideoArgs, deps: VideoDeps): Promise<VideoResult> {
  const { testCases, outputDir, trimLeadIn = true } = args;
  const result: VideoResult = { written: [], skipped: [] };
  const used = new Set<string>();

  for (const tc of testCases) {
    const scenario = tc.story.scenario;
    if (tc.status !== "passed") {
      result.skipped.push({ scenario, reason: tc.status });
      continue;
    }
    const tmp = fs.mkdtempSync(path.join(os.tmpdir(), "es-video-"));
    try {
      const input = recordingFile(tc, tmp);
      if (!input) {
        result.skipped.push({ scenario, reason: "no recording (featureVideo)" });
        continue;
      }

      const probe = await deps.exec("ffprobe", [
        "-v", "error", "-select_streams", "v:0", "-show_entries", "stream=width,height", "-of", "json", input,
      ]);
      const stream = (JSON.parse(probe.stdout) as { streams?: { width: number; height: number }[] }).streams?.[0];
      if (!stream) throw new Error(`${input} has no video stream`);
      // Playwright's WebM has no duration in its header, so the frames give it.
      const stats = await deps.exec("ffmpeg", [
        "-v", "info", "-i", input, "-vf", "negate,blackframe=amount=0:threshold=32", "-f", "null", "-",
      ]);
      const frames = parseFrames(stats.stderr);
      const lead = trimLeadIn ? leadInSeconds(frames) : 0;
      const durationMs = ((frames.at(-1)?.t ?? 0) - lead) * 1000;

      let name = scenarioSlug(scenario);
      for (let n = 2; used.has(name); n++) name = `${scenarioSlug(scenario)}-${n}`;
      used.add(name);
      fs.mkdirSync(outputDir, { recursive: true });
      const video = path.join(outputDir, `${name}.mp4`);
      // H.264 in yuv420p plays everywhere, Safari and phones included; it needs even sizes.
      await deps.exec("ffmpeg", [
        "-y", "-loglevel", "error", "-i", input, "-ss", lead.toFixed(3), "-an",
        "-vf", "scale=trunc(iw/2)*2:trunc(ih/2)*2",
        "-c:v", "libx264", "-pix_fmt", "yuv420p", "-crf", "23", "-movflags", "+faststart", video,
      ]);
      const size = { width: stream.width - (stream.width % 2), height: stream.height - (stream.height % 2) };
      const manifest = path.join(outputDir, `${name}.video.json`);
      const trimmedMs = Math.round(lead * 1000);
      fs.writeFileSync(manifest, `${JSON.stringify(videoManifest(tc, { name, size, durationMs, trimmedMs }), null, 2)}\n`);
      result.written.push({ scenario, video, manifest, trimmedMs });
    } finally {
      fs.rmSync(tmp, { recursive: true, force: true });
    }
  }

  return result;
}
